import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sprout, Sparkles, Leaf, ArrowRight, Loader2, Users, Mail, CreditCard, BadgePercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    fetchBillingPackages, fetchBillingStatus, apiSubscriptionPlans, formatApiError,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PayPalSubscribeButton from "@/components/PayPalSubscribeButton";
import { toast } from "sonner";

const ROWS = [
    { label: "Gardens",              free: "1",          community: "1",          plus: "Unlimited" },
    { label: "Beds per garden",      free: "3",          community: "Unlimited",  plus: "Unlimited" },
    { label: "Crops tracked",        free: "20",         community: "Unlimited",  plus: "Unlimited" },
    { label: "Garden photos",        free: "5",          community: "50",         plus: "100" },
    { label: "Book notes / PDFs",    free: "20 MB",      community: "150 MB",     plus: "150 MB" },
    { label: "AI chat with rooteed", free: "20 / mo",  community: "20 / mo",    plus: "Unlimited" },
    { label: "CSV import",           free: false,        community: true,         plus: true },
    { label: "Plus badge on share",  free: false,        community: true,         plus: true },
    { label: "Priority AI speed",    free: false,        community: false,        plus: true },
    { label: "Companion warnings",   free: true,         community: true,         plus: true },
    { label: "Weather & frost alerts", free: true,       community: true,         plus: true },
    { label: "Public share link",    free: true,         community: true,         plus: true },
];

function Cell({ v }) {
    if (v === true) return <Check className="w-4 h-4 text-[var(--vm-sage)] mx-auto" />;
    if (v === false) return <span className="text-[var(--vm-text-muted)] opacity-50">—</span>;
    return <span>{v}</span>;
}

export default function Pricing() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [pkgs, setPkgs] = useState(null);
    const [status, setStatus] = useState(null);
    const [subPlans, setSubPlans] = useState(null);
    const [busy, setBusy] = useState(false);
    const [chosenPlan, setChosenPlan] = useState("annual"); // default to best-value highlight

    useEffect(() => {
        fetchBillingPackages().then(setPkgs).catch(() => {});
        apiSubscriptionPlans().then(setSubPlans).catch(() => {});
        if (user && user !== false) fetchBillingStatus().then(setStatus).catch(() => {});
    }, [user]);

    const tier = status?.tier || user?.tier;
    const isPlus = tier === "plus";
    const isCommunity = tier === "community";
    const monthlyPlan = (subPlans?.plans || []).find(p => p.key === "monthly");
    const annualPlan = (subPlans?.plans || []).find(p => p.key === "annual");
    const trialDays = subPlans?.trial_days || 4;

    const onSubscribed = async () => {
        await refresh();
        navigate("/billing");
    };

    return (
        <div className="min-h-screen paper-texture py-16 px-6" data-testid="pricing-page">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-14">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Sprout className="w-8 h-8 text-[var(--vm-accent)]" strokeWidth={1.5} />
                        <span className="overline text-[var(--vm-sage)]">Plans</span>
                    </div>
                    <h1 className="font-serif-display text-5xl lg:text-6xl text-[var(--vm-text)] mb-4">
                        Grow without limits
                    </h1>
                    <p className="text-[var(--vm-text-secondary)] max-w-xl mx-auto">
                        Pick monthly or annual — your <strong>{trialDays}-day free trial</strong> starts right after you confirm.
                        No charge for {trialDays} days. Cancel anytime during the trial.
                    </p>
                </div>

                {new URLSearchParams(window.location.search).get("welcome") === "1" && (
                    <div className="mb-8 mx-auto max-w-2xl p-4 rounded-lg bg-[var(--vm-sage)]/10 border border-[var(--vm-sage)]/30 text-center" data-testid="post-register-welcome">
                        <Sprout className="inline w-4 h-4 text-[var(--vm-sage)] mr-1" strokeWidth={1.5} />
                        <span className="text-sm text-[var(--vm-text-secondary)]">
                            Welcome to rooteed! Pick a plan below to start your <strong>{trialDays}-day free trial</strong> — no charge until day {trialDays + 1}.
                        </span>
                    </div>
                )}

                <div className="grid md:grid-cols-3 gap-6 mb-12">
                    {/* FREE */}
                    <div className="journal-card p-8" data-testid="plan-free">
                        <div className="flex items-center gap-2 mb-1">
                            <Leaf className="w-5 h-5 text-[var(--vm-sage)]" strokeWidth={1.5} />
                            <span className="overline text-[var(--vm-sage)]">Existing only</span>
                        </div>
                        <h2 className="font-serif-display text-4xl text-[var(--vm-text)] mb-2">Seedling</h2>
                        <div className="flex items-baseline gap-1 mb-6">
                            <span className="font-serif-display text-5xl text-[var(--vm-text)]">€0</span>
                            <span className="text-[var(--vm-text-muted)]">/ forever</span>
                        </div>
                        <p className="text-sm text-[var(--vm-text-secondary)] mb-6">
                            Closed to new signups. Existing free users keep access to a single garden,
                            a handful of crops, and weekly AI chats — forever.
                        </p>
                        {!user || user === false ? (
                            <Button disabled variant="outline" className="w-full rounded-full border-[var(--vm-border)]" data-testid="free-closed-btn">
                                New signups go to Plus
                            </Button>
                        ) : (
                            <Button disabled variant="outline" className="w-full rounded-full border-[var(--vm-border)]">
                                {tier === "free" ? "Your current plan" : "Included"}
                            </Button>
                        )}
                    </div>

                    {/* COMMUNITY */}
                    <div
                        className="journal-card p-8 relative border border-[var(--vm-sage)]/40"
                        data-testid="plan-community"
                    >
                        <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[var(--vm-sage)] text-[var(--vm-bg)] font-semibold">
                            Invite only
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                            <Users className="w-5 h-5 text-[var(--vm-sage)]" strokeWidth={1.5} />
                            <span className="overline text-[var(--vm-sage)]">Community gardens</span>
                        </div>
                        <h2 className="font-serif-display text-4xl text-[var(--vm-text)] mb-2">Allotment</h2>
                        <div className="flex items-baseline gap-1 mb-6">
                            <span className="font-serif-display text-5xl text-[var(--vm-text)]">€0</span>
                            <span className="text-[var(--vm-text-muted)]">/ verified projects</span>
                        </div>
                        <p className="text-sm text-[var(--vm-text-secondary)] mb-6">
                            For allotments, school gardens and community projects. One garden,
                            unlimited beds & crops, 50 photos, CSV import, Plus badge on share.
                            No priority AI.
                        </p>
                        {isCommunity ? (
                            <Button disabled className="w-full rounded-full bg-[var(--vm-sage)] text-[var(--vm-bg)]" data-testid="community-current-btn">
                                You're verified
                            </Button>
                        ) : (
                            <Button
                                asChild variant="outline"
                                className="w-full rounded-full border-[var(--vm-sage)] text-[var(--vm-sage)]"
                                data-testid="community-apply-btn"
                            >
                                <a href="mailto:gnemeth1984@gmail.com?subject=rooteed%20Community%20Garden%20verification&body=Hi%20rooteed%20team%2C%0A%0AI'd%20like%20verification%20for%20our%20community%20garden%20project.%0A%0AOrganisation%20name%3A%20%0AContact%20person%3A%20%0ALocation%3A%20%0AAccount%20email%20registered%20with%20rooteed%3A%20%0A%0AThanks!">
                                    <Mail className="w-4 h-4 mr-2" /> Request verification
                                </a>
                            </Button>
                        )}
                    </div>

                    {/* PLUS — subscription (4-day trial → €6.95/mo or €59.95/yr) */}
                    <div
                        className="journal-card p-8 relative border-2 border-[var(--vm-accent)]"
                        data-testid="plan-plus"
                    >
                        <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[var(--vm-accent)] text-[var(--vm-bg)] font-semibold">
                            {trialDays}-day free trial
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-5 h-5 text-[var(--vm-accent)]" strokeWidth={1.5} />
                            <span className="overline text-[var(--vm-accent)]">rooteed Plus</span>
                        </div>
                        <h2 className="font-serif-display text-4xl text-[var(--vm-text)] mb-2">Harvest</h2>
                        <p className="text-sm text-[var(--vm-text-secondary)] mb-5">
                            Unlimited gardens, crops, 100 photos, 150 MB notes, unlimited chat
                            and priority AI. <strong>{trialDays} days free, cancel anytime during the trial.</strong>
                        </p>

                        {/* Plan toggle */}
                        <div className="mb-4 grid grid-cols-2 gap-2" data-testid="plan-toggle">
                            <button
                                type="button"
                                onClick={() => setChosenPlan("monthly")}
                                data-testid="choose-monthly"
                                className={`text-left p-3 rounded-lg border transition-colors ${
                                    chosenPlan === "monthly"
                                        ? "border-[var(--vm-accent)] bg-[var(--vm-accent)]/10"
                                        : "border-[var(--vm-border)] hover:border-[var(--vm-accent)]/40"
                                }`}
                            >
                                <div className="text-[10px] uppercase tracking-wider text-[var(--vm-text-muted)]">Monthly</div>
                                <div className="font-serif-display text-2xl text-[var(--vm-text)]">€{monthlyPlan?.price_eur ?? "6.95"}</div>
                                <div className="text-[10px] text-[var(--vm-text-muted)]">per month</div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setChosenPlan("annual")}
                                data-testid="choose-annual"
                                className={`text-left p-3 rounded-lg border transition-colors relative ${
                                    chosenPlan === "annual"
                                        ? "border-[var(--vm-accent)] bg-[var(--vm-accent)]/10"
                                        : "border-[var(--vm-border)] hover:border-[var(--vm-accent)]/40"
                                }`}
                            >
                                <div className="absolute -top-2 right-2 px-1.5 py-0.5 rounded text-[9px] uppercase bg-[var(--vm-sage)] text-[var(--vm-bg)] font-semibold flex items-center gap-1">
                                    <BadgePercent className="w-2.5 h-2.5" /> Save 28%
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-[var(--vm-text-muted)]">Annual</div>
                                <div className="font-serif-display text-2xl text-[var(--vm-text)]">€{annualPlan?.price_eur ?? "59.95"}</div>
                                <div className="text-[10px] text-[var(--vm-text-muted)]">per year · €5/mo</div>
                            </button>
                        </div>

                        {isPlus ? (
                            <Button asChild className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                                <Link to="/billing" data-testid="manage-plus-btn">
                                    Manage your Plus <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                            </Button>
                        ) : !user || user === false ? (
                            <Button asChild className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]" data-testid="register-to-subscribe-btn">
                                <Link to="/register">
                                    Start {trialDays}-day free trial <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                            </Button>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-xs text-[var(--vm-text-muted)] text-center">
                                    No charge for {trialDays} days · cancel anytime
                                </p>
                                <PayPalSubscribeButton
                                    planKey={chosenPlan}
                                    onSuccess={onSubscribed}
                                    disabled={busy}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Comparison table */}
                <div className="journal-card p-2 overflow-hidden">
                    <table className="w-full text-sm" data-testid="plan-comparison">
                        <thead>
                            <tr className="border-b border-[var(--vm-border)]">
                                <th className="text-left px-4 py-3 font-medium text-[var(--vm-text-muted)] uppercase text-xs tracking-wider">Feature</th>
                                <th className="text-center px-4 py-3 font-medium text-[var(--vm-sage)] uppercase text-xs tracking-wider">Free</th>
                                <th className="text-center px-4 py-3 font-medium text-[var(--vm-sage)] uppercase text-xs tracking-wider">Community</th>
                                <th className="text-center px-4 py-3 font-medium text-[var(--vm-accent)] uppercase text-xs tracking-wider">Plus</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ROWS.map((row, i) => (
                                <tr key={row.label} className={i % 2 === 0 ? "bg-[var(--vm-card)]/40" : ""}>
                                    <td className="px-4 py-3 text-[var(--vm-text)]">{row.label}</td>
                                    <td className="px-4 py-3 text-center"><Cell v={row.free} /></td>
                                    <td className="px-4 py-3 text-center"><Cell v={row.community} /></td>
                                    <td className="px-4 py-3 text-center text-[var(--vm-accent)]"><Cell v={row.plus} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="text-center text-xs text-[var(--vm-text-muted)] mt-10">
                    Payments by PayPal (or any debit/credit card via PayPal — no PayPal account needed).
                    {" "}4-day free trial then €6.95/month or €59.95/year. Cancel anytime in PayPal — no refunds after the trial,
                    but Plus stays active until your current period ends.
                    Community tier is verified manually — email
                    <a className="text-[var(--vm-sage)] underline mx-1" href="mailto:gnemeth1984@gmail.com">gnemeth1984@gmail.com</a>
                    for access.
                </p>
            </div>
        </div>
    );
}
